import React, { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  STICKY_GLASS_CHROME_CLASSNAME,
  STICKY_GLASS_FOOTER_SHADOW_CLASSNAME,
  STICKY_GLASS_HEADER_SHADOW_CLASSNAME,
  observeElementScrollEdges,
  type ElementScrollEdges,
} from '../lib/sticky-glass';

export const ENTITY_DETAIL_DIALOG_CONTENT_CLASSNAME =
  'flex max-h-[90vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-[900px]';

export const ENTITY_DETAIL_BODY_SCROLL_ATTR = 'data-entity-detail-scroll';

export const ENTITY_DETAIL_MODAL_HEADER_TITLE_CLASSNAME =
  '[&_h1]:!text-lg [&_h2]:!text-lg [&_h1]:!font-semibold [&_h2]:!font-semibold [&_input]:!text-lg [&_input]:!font-semibold';

export const ENTITY_DETAIL_MODAL_HEADER_CHROME_CLASSNAME = 'px-4 py-3 pr-12';

export const ENTITY_DETAIL_MODAL_FOOTER_CHROME_CLASSNAME = 'px-4 py-3';

export const ENTITY_DETAIL_MODAL_CLOSE_CLASSNAME =
  'absolute right-4 top-1/2 z-50 -translate-y-1/2 rounded-sm border-0 bg-transparent p-0 opacity-70 ring-offset-background transition-opacity hover:bg-accent hover:text-accent-foreground hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none';

const INITIAL_SCROLL_EDGES: ElementScrollEdges = {
  canScrollUp: false,
  canScrollDown: false,
};

interface EntityDetailModalLayoutProps {
  header?: React.ReactNode;
  footer?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  containBody?: boolean;
  overlayChrome?: boolean;
}

export const EntityDetailModalTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => <h2 className="m-0 text-foreground">{children}</h2>;

const EntityDetailModalCloseButton: React.FC = () => (
  <button
    type="button"
    data-dialog-close=""
    data-entity-detail-dialog-close=""
    className={ENTITY_DETAIL_MODAL_CLOSE_CLASSNAME}
    onClick={(event) => {
      const dialog = event.currentTarget.closest('[role="dialog"]');
      if (!dialog) {
        return;
      }
      const nativeClose = Array.from(dialog.children).find(
        (child): child is HTMLButtonElement =>
          child instanceof HTMLButtonElement
          && child.hasAttribute('data-dialog-close')
          && child !== event.currentTarget,
      );
      if (nativeClose) {
        nativeClose.click();
        return;
      }
      dialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    }}
  >
    <X className="h-4 w-4" />
    <span className="sr-only">Close</span>
  </button>
);

export const EntityDetailModalLayout: React.FC<EntityDetailModalLayoutProps> = ({
  header,
  footer,
  children,
  className,
  containBody = false,
  overlayChrome = false,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [scrollEdges, setScrollEdges] = useState<ElementScrollEdges>(INITIAL_SCROLL_EDGES);
  const [chromeHeights, setChromeHeights] = useState({ header: 0, footer: 0 });
  const bodyCanScroll = scrollEdges.canScrollUp || scrollEdges.canScrollDown;

  useEffect(() => {
    if (overlayChrome) {
      return;
    }
    const element = scrollRef.current;
    if (!element || containBody) {
      setScrollEdges(INITIAL_SCROLL_EDGES);
      return;
    }
    return observeElementScrollEdges(element, setScrollEdges);
  }, [containBody, overlayChrome]);

  useEffect(() => {
    if (!overlayChrome) {
      return;
    }
    const body = bodyRef.current;
    if (!body) {
      return;
    }
    const findScrollElement = (): HTMLElement | null => {
      const marked = body.querySelector<HTMLElement>(`[${ENTITY_DETAIL_BODY_SCROLL_ATTR}]`);
      if (marked) {
        return marked;
      }
      return body;
    };

    let stopObserving: (() => void) | undefined;
    const attach = (): void => {
      stopObserving?.();
      const element = findScrollElement();
      if (!element) {
        setScrollEdges(INITIAL_SCROLL_EDGES);
        return;
      }
      stopObserving = observeElementScrollEdges(element, setScrollEdges);
    };

    attach();
    const mutationObserver = new MutationObserver(attach);
    mutationObserver.observe(body, { childList: true, subtree: true });
    return () => {
      stopObserving?.();
      mutationObserver.disconnect();
    };
  }, [overlayChrome]);

  useEffect(() => {
    if (!overlayChrome) {
      setChromeHeights({ header: 0, footer: 0 });
      return;
    }
    const updateHeights = (): void => {
      setChromeHeights({
        header: headerRef.current?.offsetHeight ?? 0,
        footer: footerRef.current?.offsetHeight ?? 0,
      });
    };
    updateHeights();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(updateHeights);
    if (headerRef.current) {
      observer.observe(headerRef.current);
    }
    if (footerRef.current) {
      observer.observe(footerRef.current);
    }
    return () => observer.disconnect();
  }, [overlayChrome, header, footer]);

  if (overlayChrome) {
    return (
      <div
        className={cn(
          'relative flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background text-foreground',
          className
        )}
        style={
          {
            '--entity-detail-header-height': `${chromeHeights.header}px`,
            '--entity-detail-footer-height': `${chromeHeights.footer}px`,
          } as React.CSSProperties
        }
      >
        <div ref={bodyRef} className="absolute inset-0 flex min-h-0 min-w-0 flex-col overflow-hidden">
          {children}
        </div>
        {header ? (
          <div
            ref={headerRef}
            className={cn(
              'absolute inset-x-0 top-0 z-20',
              bodyCanScroll && 'border-b',
              ENTITY_DETAIL_MODAL_HEADER_CHROME_CLASSNAME,
              ENTITY_DETAIL_MODAL_HEADER_TITLE_CLASSNAME,
              STICKY_GLASS_CHROME_CLASSNAME,
              scrollEdges.canScrollUp && STICKY_GLASS_HEADER_SHADOW_CLASSNAME
            )}
          >
            {header}
            <EntityDetailModalCloseButton />
          </div>
        ) : null}
        {footer ? (
          <div
            ref={footerRef}
            className={cn(
              'absolute inset-x-0 bottom-0 z-20',
              bodyCanScroll && 'border-t',
              ENTITY_DETAIL_MODAL_FOOTER_CHROME_CLASSNAME,
              STICKY_GLASS_CHROME_CLASSNAME,
              scrollEdges.canScrollDown && STICKY_GLASS_FOOTER_SHADOW_CLASSNAME
            )}
          >
            {footer}
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex h-full min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background text-foreground',
        className
      )}
    >
      <div
        ref={scrollRef}
        className={cn(
          'min-h-0 flex-1 overflow-x-hidden',
          containBody ? 'overflow-y-hidden' : 'overflow-y-auto'
        )}
      >
        <div
          className={cn(
            'flex flex-col',
            containBody ? 'h-full min-h-0 overflow-hidden' : 'min-h-full'
          )}
        >
          {header ? (
            <div
              className={cn(
                'z-10 shrink-0',
                bodyCanScroll && 'border-b',
                ENTITY_DETAIL_MODAL_HEADER_CHROME_CLASSNAME,
                ENTITY_DETAIL_MODAL_HEADER_TITLE_CLASSNAME,
                containBody ? 'relative' : 'sticky top-0',
                STICKY_GLASS_CHROME_CLASSNAME,
                !containBody && scrollEdges.canScrollUp && STICKY_GLASS_HEADER_SHADOW_CLASSNAME
              )}
            >
              {header}
              <EntityDetailModalCloseButton />
            </div>
          ) : null}
          <div
            className={cn(
              'min-w-0 flex-1',
              containBody && 'flex min-h-0 flex-col overflow-hidden'
            )}
          >
            {children}
          </div>
          {footer ? (
            <div
              className={cn(
                'z-10 shrink-0',
                bodyCanScroll && 'border-t',
                ENTITY_DETAIL_MODAL_FOOTER_CHROME_CLASSNAME,
                containBody ? 'relative' : 'sticky bottom-0',
                STICKY_GLASS_CHROME_CLASSNAME,
                !containBody && scrollEdges.canScrollDown && STICKY_GLASS_FOOTER_SHADOW_CLASSNAME
              )}
            >
              {footer}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
